/**
 * Theme Context
 *
 * Provides tenant theme configuration throughout the app.
 * Loads theme from API and injects CSS variables into document root.
 */
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Theme, TenantInfo } from '@/types/theme';
import { fetchCurrentTenantTheme } from '@/services/themeService';
import { generateColorVariants, getContrastTextColor } from '@/utils/colorUtils';

interface ThemeContextValue {
  theme: Theme | null;
  tenant: TenantInfo | null;
  loading: boolean;
  error: string | null;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

// Theme cache constants
const THEME_CACHE_KEY = 'tenant_theme_cache';
const THEME_CACHE_TTL = 3600000; // 1 hour in milliseconds

/**
 * Apply theme to document by injecting CSS variables
 */
const applyThemeToDocument = (theme: Theme) => {
  const root = document.documentElement;

  // Core colors
  root.style.setProperty('--color-primary', theme.primary_color);
  root.style.setProperty('--color-secondary', theme.secondary_color);

  // Always generate color variants dynamically from the primary color
  // This ensures hover colors always match the current primary color
  const primaryVariants = generateColorVariants(theme.primary_color);
  root.style.setProperty('--color-primary-hover', primaryVariants.hover);
  root.style.setProperty('--color-primary-light', primaryVariants.light);
  root.style.setProperty('--color-primary-dark', primaryVariants.dark);

  // Calculate contrast text color for primary (for buttons with primary background)
  const primaryTextColor = getContrastTextColor(theme.primary_color);
  root.style.setProperty('--color-primary-text', primaryTextColor);

  // Generate secondary color variants
  const secondaryVariants = generateColorVariants(theme.secondary_color);
  root.style.setProperty('--color-secondary-hover', secondaryVariants.hover);
  root.style.setProperty('--color-secondary-light', secondaryVariants.light);

  root.style.setProperty('--color-success', theme.colors.success);
  root.style.setProperty('--color-success-hover', theme.colors.success_hover);
  root.style.setProperty('--color-warning', theme.colors.warning);
  root.style.setProperty('--color-warning-hover', theme.colors.warning_hover);
  root.style.setProperty('--color-danger', theme.colors.danger);
  root.style.setProperty('--color-danger-hover', theme.colors.danger_hover);
  root.style.setProperty('--color-info', theme.colors.info);
  root.style.setProperty('--color-info-hover', theme.colors.info_hover);

  root.style.setProperty('--color-text-primary', theme.colors.text_primary);
  root.style.setProperty('--color-text-secondary', theme.colors.text_secondary);
  root.style.setProperty('--color-text-muted', theme.colors.text_muted);

  root.style.setProperty('--color-bg-primary', theme.colors.background_primary);
  root.style.setProperty('--color-bg-secondary', theme.colors.background_secondary);
  root.style.setProperty('--color-bg-tertiary', theme.colors.background_tertiary);

  // Add subtle background variant (very light primary color for containers)
  // Import lightenColor utility for custom lightening
  const rgb = theme.primary_color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (rgb) {
    const r = parseInt(rgb[1], 16);
    const g = parseInt(rgb[2], 16);
    const b = parseInt(rgb[3], 16);
    // Lighten by 95% for very subtle background
    const factor = 0.95;
    const subtleR = Math.round(r + (255 - r) * factor);
    const subtleG = Math.round(g + (255 - g) * factor);
    const subtleB = Math.round(b + (255 - b) * factor);
    const subtleColor = `#${[subtleR, subtleG, subtleB].map(x => x.toString(16).padStart(2, '0')).join('')}`;
    root.style.setProperty('--color-background-subtle', subtleColor);
  } else {
    root.style.setProperty('--color-background-subtle', primaryVariants.light);
  }

  root.style.setProperty('--color-border', theme.colors.border_color);
  root.style.setProperty('--color-border-focus', theme.colors.border_focus);

  // Typography
  root.style.setProperty('--font-primary', theme.font_primary);
  root.style.setProperty('--font-heading', theme.font_heading);
  root.style.setProperty('--font-mono', theme.typography.font_mono);

  root.style.setProperty('--font-size-xs', theme.typography.font_size_xs);
  root.style.setProperty('--font-size-sm', theme.typography.font_size_sm);
  root.style.setProperty('--font-size-base', theme.typography.font_size_base);
  root.style.setProperty('--font-size-lg', theme.typography.font_size_lg);
  root.style.setProperty('--font-size-xl', theme.typography.font_size_xl);
  root.style.setProperty('--font-size-2xl', theme.typography.font_size_2xl);
  root.style.setProperty('--font-size-3xl', theme.typography.font_size_3xl);

  root.style.setProperty('--font-weight-normal', theme.typography.font_weight_normal);
  root.style.setProperty('--font-weight-medium', theme.typography.font_weight_medium);
  root.style.setProperty('--font-weight-semibold', theme.typography.font_weight_semibold);
  root.style.setProperty('--font-weight-bold', theme.typography.font_weight_bold);

  // Spacing
  root.style.setProperty('--spacing-xs', theme.spacing.xs);
  root.style.setProperty('--spacing-sm', theme.spacing.sm);
  root.style.setProperty('--spacing-md', theme.spacing.md);
  root.style.setProperty('--spacing-lg', theme.spacing.lg);
  root.style.setProperty('--spacing-xl', theme.spacing.xl);
  root.style.setProperty('--spacing-2xl', theme.spacing['2xl']);

  // Border radius
  root.style.setProperty('--radius-sm', theme.border_radius.sm);
  root.style.setProperty('--radius-md', theme.border_radius.md);
  root.style.setProperty('--radius-lg', theme.border_radius.lg);
  root.style.setProperty('--radius-xl', theme.border_radius.xl);
  root.style.setProperty('--radius-full', theme.border_radius.full);

  // Shadows
  root.style.setProperty('--shadow-sm', theme.shadows.sm);
  root.style.setProperty('--shadow-md', theme.shadows.md);
  root.style.setProperty('--shadow-lg', theme.shadows.lg);
  root.style.setProperty('--shadow-xl', theme.shadows.xl);

  // Transitions
  root.style.setProperty('--transition-fast', theme.transitions.fast);
  root.style.setProperty('--transition-base', theme.transitions.base);
  root.style.setProperty('--transition-slow', theme.transitions.slow);

  // Apply primary font to body
  document.body.style.fontFamily = theme.font_primary;
};

// Apply cached theme immediately on module load (before React renders)
// This prevents any flash of unstyled content
try {
  const cachedData = sessionStorage.getItem(THEME_CACHE_KEY);
  if (cachedData) {
    const { theme: cachedTheme, timestamp } = JSON.parse(cachedData);
    const now = Date.now();

    // Check if cache is still valid
    if (now - timestamp < THEME_CACHE_TTL) {
      // Apply theme synchronously before any rendering
      applyThemeToDocument(cachedTheme);
    }
  }
} catch (error) {
  console.warn('⚠️ Failed to apply cached theme synchronously:', error);
}

// Helper to get cached theme for initial state
const getCachedThemeData = () => {
  try {
    const cachedData = sessionStorage.getItem(THEME_CACHE_KEY);
    if (cachedData) {
      const { theme, tenant, timestamp } = JSON.parse(cachedData);
      const now = Date.now();
      if (now - timestamp < THEME_CACHE_TTL) {
        return { theme, tenant };
      }
    }
  } catch (error) {
    console.warn('Failed to get cached theme for initial state:', error);
  }
  return { theme: null, tenant: null };
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Location removed - not used in this component

  // Initialize state with cached data to prevent unnecessary re-renders
  const cachedData = getCachedThemeData();
  const [theme, setTheme] = useState<Theme | null>(cachedData.theme);
  const [tenant, setTenant] = useState<TenantInfo | null>(cachedData.tenant);
  const [loading, setLoading] = useState(!cachedData.theme); // Not loading if we have cached theme
  const [error, setError] = useState<string | null>(null);

  const loadTheme = async () => {
    // CRITICAL: Check cache validity FIRST before ANY state changes
    // This prevents unnecessary API calls even in StrictMode
    const cachedData = sessionStorage.getItem(THEME_CACHE_KEY);
    if (cachedData) {
      try {
        const { timestamp } = JSON.parse(cachedData);
        const now = Date.now();

        // If cache is valid, we don't need to do ANYTHING
        // The theme was already applied by module-level code and state was initialized
        if (now - timestamp < THEME_CACHE_TTL) {
          return; // Exit immediately without any state changes
        }
      } catch (parseError) {
        console.warn('⚠️ Failed to parse cached theme, fetching fresh data...');
      }
    }

    // Only reach here if no valid cache exists
    try {
      setLoading(true);
      setError(null);

      console.log('🎨 Loading theme from API...');
      const response = await fetchCurrentTenantTheme();
      setTheme(response.theme);
      setTenant(response.tenant);

      // Cache the theme data in sessionStorage
      sessionStorage.setItem(
        THEME_CACHE_KEY,
        JSON.stringify({
          theme: response.theme,
          tenant: response.tenant,
          timestamp: Date.now(),
        })
      );

      // Apply theme to document
      applyThemeToDocument(response.theme);

      console.log(`✅ Theme loaded for tenant: ${response.tenant.name}`);
    } catch (err: any) {
      console.error('❌ Failed to load theme:', err);
      setError('Failed to load theme');
    } finally {
      setLoading(false);
    }
  };

  // Check if we should skip theme loading (on admin/public pages only)
  const shouldSkipThemeLoading = () => {
    const path = window.location.pathname;

    // Only skip theme loading on admin portal pages (public schema, no tenant)
    // Login pages SHOULD load theme for tenant branding
    const skipPaths = [
      '/admin/', // All admin portal pages (public schema, no tenant)
      '/upload/', // Public upload page
    ];

    return skipPaths.some(skipPath => path.startsWith(skipPath));
  };

  // Load theme only once on mount (cached in sessionStorage for subsequent navigations)
  useEffect(() => {
    // Skip theme loading on admin/public pages
    if (shouldSkipThemeLoading()) {
      setLoading(false);
      return;
    }

    // Check if we have valid cached data
    const cachedData = sessionStorage.getItem(THEME_CACHE_KEY);
    if (cachedData) {
      try {
        const { timestamp } = JSON.parse(cachedData);
        const now = Date.now();

        // If cache is valid, we already applied it in module-level code
        // and initialized state with it - no need to do anything
        if (now - timestamp < THEME_CACHE_TTL && theme && tenant) {
          return;
        }
      } catch (error) {
        console.warn('Failed to parse cache in useEffect:', error);
      }
    }

    // Only call loadTheme if we don't have cached data
    console.log('📡 No valid cache - calling loadTheme()');
    loadTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const refreshTheme = async () => {
    // Clear cache to force fresh fetch
    sessionStorage.removeItem(THEME_CACHE_KEY);
    await loadTheme();
  };

  const value: ThemeContextValue = {
    theme,
    tenant,
    loading,
    error,
    refreshTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Hook to access theme context
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
